import { getLateFee, getMilestoneFreeRoles, patchMilestoneFreeRoles } from "../services/loanApi";
import { info, error as logAppInsightError } from '../utils/logger';
import { logError } from "../utils/logError";
import { getCurrentPSTDateAndTimeISO } from '../utils/formatDate';
import { getOrgConfig } from './organizationConfigUtil';

const toDateOnly = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt;
};

export const getActiveLicenseForState = (licenses, propertyState) => {
    if (!propertyState) return null;
    if (!Array.isArray(licenses) || licenses.length === 0) return null;

    const st = String(propertyState).trim().toUpperCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
        licenses
            .filter((lic) => lic?.selected === true)
            .filter((lic) => String(lic?.stateAbbreviation || "").trim().toUpperCase() === st)
            .find((lic) => {
                const start = toDateOnly(lic?.startDate);
                const end = toDateOnly(lic?.endDate);

                if (!start && !end) return true;
                if (start && !end) return start <= today;
                if (start && end) return today >= start && today <= end;
                return false;
            }) || null
    );
};

export const extractCompanyDetails = (company, prefix = 'company') => ({
    [`${prefix}Name`]: company?.organizationName ?? null,
    [`${prefix}ID`]: company?.tpoId ?? null,
    [`${prefix}OrganizationID`]: company?.orgId ?? null,
    [`${prefix}LegalName`]: company?.companyLegalName ?? null,
    [`${prefix}DbaName`]: company?.dba?.dbaDetails?.[0]?.name ?? null,
    [`${prefix}Address`]: company?.address?.street1 ?? null,
    [`${prefix}City`]: company?.address?.city ?? null,
    [`${prefix}State`]: company?.address?.state ?? null,
    [`${prefix}Zip`]: company?.address?.zip ?? null,
    [`${prefix}Phone`]: company?.phoneNumber ?? null,
    [`${prefix}Fax`]: company?.faxNumber ?? null,
    [`${prefix}AeName`]: company?.primarySalesRepAe?.name ?? null,
    [`${prefix}AeUserName`]: company?.primarySalesRepAe?.userId ?? null,
    [`${prefix}Rating`]: company?.approvalStatus?.companyRating ?? null
});

export const extractUserDetails = (user, type, selectedCompanyData) => ({
    [`${type}Name`]: user?.name ?? null,
    [`${type}Id`]: user?.id ?? null,
    [`${type}Email`]: user?.email ?? null,
    [`${type}Address`]: user?.address ?? null,
    [`${type}City`]: user?.city ?? null,
    [`${type}State`]: user?.state ?? null,
    [`${type}Zip`]: user?.zip ?? null,
    [`${type}AeName`]: selectedCompanyData?.primarySalesRepAe?.name ?? null,
    [`${type}AeUserName`]: user?.srAeUserId ?? null
});

export const buildInterviewerName = (officer) => {
    if (!officer) return null;

    const { firstName, middleName, lastName, suffix } = officer;

    const nameParts = [
        firstName?.trim(),
        middleName?.trim(),
        lastName?.trim(),
        suffix?.trim()
    ].filter(Boolean);

    return nameParts.join(' ');
};

export const applyStaticImportParityForManual = (
    loanObject,
    {
        loanType,
        selectedCompanyData,
        loLicense
    }
) => {
    if (!loanObject) return loanObject;

    // Ensure minimal structure (manual has nothing)
    loanObject.applications = loanObject.applications ?? [];
    loanObject.contacts = loanObject.contacts ?? [];

    // 1️ Business → Investor (import does this)
    if ((loanType || "").toLowerCase() === "business") {
        if (!loanObject.applications.length) {
            loanObject.applications.push({});
        }
        loanObject.applications[0].propertyUsageType = "Investor";
    }

    // 2️ BROKER_LENDER.companyId ← Company NMLS (import does this)
    const brokerLender = loanObject.contacts.find(
        c => c.contactType === "BROKER_LENDER"
    );

    if (brokerLender && selectedCompanyData?.businessInformation?.nmlsId) {
        brokerLender.companyId =
            selectedCompanyData.businessInformation.nmlsId;
    }

    // 3️ Company state license (import does this)
    if (selectedCompanyData?.license?.licenseNumber) {
        loanObject.interviewersCompanyStateLicense =
            selectedCompanyData.license.licenseNumber;
    }

    // 4️ Loan Officer license (import does this)
    loanObject.interviewerLicenseIdentifier =
        loLicense?.licenseNumber || "";

    return loanObject;
};

export const getProgressWidth = (step) => {
    switch (step) {
        case 1: return 20;
        case 2: return 40;
        case 3: return 60;
        case 4: return 80;
        case 5: return 100;
        default: return 0;
    }
};

export const getProgressWidthChannels = (step) => {
    switch (step) {
        case 0: return 0;
        case 1: return 20;
        case 2: return 40;
        case 3: return 60;
        case 4: return 80;
        case 5: return 100;
        default: return 0;
    }
};

export const formatLoanErrorMessage = ({ errorCode, apiData, error }) => {
    let parsedErrors = [];
    if (apiData?.details) {
        parsedErrors.push(apiData.details);
    }

    if (Array.isArray(apiData?.errors) && apiData.errors.length > 0) {
        apiData.errors.forEach((e, idx) => {
            const summary = e.summary || `Error ${idx + 1}`;
            const details = e.details || "";
            parsedErrors.push(`${summary}${details ? " — " + details : ""}`);
        });
    } else if (apiData?.summary) {
        parsedErrors.push(`${apiData.summary}${apiData.details ? " — " + apiData.details : ""}`);
    } else if (error?.message) {
        parsedErrors.push(error.message);
    } else {
        parsedErrors.push("An unexpected error occurred while creating the loan.");
    }

    const topDetail =
        apiData?.details ||
        apiData?.summary ||
        parsedErrors[0] ||
        "An unexpected error occurred while creating the loan.";

    let remainingParagraphs = [];
    if (Array.isArray(apiData?.errors)) {
        remainingParagraphs = apiData.errors.map(e => {
            const summary = e.summary || "";
            const details = e.details ? `\n— ${e.details}` : "";
            return `${summary}${details}`;
        });
    }

    let message = `Error Code: ${errorCode}\n${topDetail}`;
    if (remainingParagraphs.length > 0) {
        message += `\n\n${remainingParagraphs.join("\n\n")}`;
    }

    return { message, parsedErrors };
};

export const parseErrorModalBlocks = (errorMessage) => {
    const raw = (errorMessage || "").trim();
    if (!raw) return null;

    let parts = raw.split(/\r?\n\r?\n/).map(p => p.trim()).filter(Boolean);
    if (parts.length === 0 && raw) {
        parts = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    }

    let heading = "";
    let general = "";
    let remaining = [];

    const firstPart = parts[0] || "";
    const errMatch = firstPart.match(/(Error Code:\s*\S+)/i);

    if (errMatch) {
        heading = errMatch[1].trim();
        const leftover = firstPart.replace(errMatch[1], '').trim();

        if (leftover) {
            const lines = leftover.split(/\r?\n/).filter(Boolean);
            general = lines.shift() || "";
            remaining = [...lines, ...parts.slice(1)];
        } else {
            general = parts[1] || "";
            remaining = parts.slice(2);
        }
    } else {
        heading = "";
        general = firstPart;
        remaining = parts.slice(1);
    }

    remaining = remaining.flatMap(b =>
        b.split(/\r?\n\r?\n/).map(x => x.trim()).filter(Boolean)
    );

    return { heading, general, remaining };
};

export const parseErrorBlock = (blk) => {
    const lines = String(blk || "")
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean);

    const firstLine = lines[0] || "";
    const restLines = lines.slice(1);

    let fieldToken = firstLine;
    let desc = restLines.join(" ");

    // Support inline " — "
    const inlineDashSplit = firstLine.split(/\s+—\s+/);
    if (inlineDashSplit.length > 1) {
        fieldToken = inlineDashSplit[0].trim();
        desc = inlineDashSplit.slice(1).join(" — ").trim();
    }

    if (!desc && restLines.length > 0) {
        desc = restLines.join(" ");
    }

    return { fieldToken, desc };
};

/*
 * MISMO import - Estimated Net Monthly Rental Income
 * (URLA.X81 / loan.rentalEstimatedNetMonthlyRentAmount).
 */

// SkyTPO: the value is only kept when the file has REO properties
// (org flag clearImportedRentalIncomeWithoutReo). Call before createLoan.
export const clearRentalIncomeWithoutReo = (loanObject) => {
    if (!loanObject || !getOrgConfig().clearImportedRentalIncomeWithoutReo) return;

    const reoProperties = loanObject.applications?.[0]?.reoProperties;
    const hasReoProperties = Array.isArray(reoProperties) && reoProperties.length > 0;

    if (!hasReoProperties) {
        delete loanObject.rentalEstimatedNetMonthlyRentAmount;
    }
};

// URLA.X81 is a lock field, so createLoan does not set it from the loan
// object - write it (locked) with fieldWriter once the loan exists.
// SkyTPO only (same org flag); returns [] for other orgs or when there is no value.
export const getImportedRentalIncomeFieldWrite = (loanObject) => {
    if (!getOrgConfig().clearImportedRentalIncomeWithoutReo) return [];

    const value = loanObject?.rentalEstimatedNetMonthlyRentAmount;
    if (value === null || value === undefined || String(value).trim() === "") return [];

    return [{ id: "URLA.X81", value: String(value), lock: true }];
};

export const addFieldsToDictionary = (fieldDictionary, fields) => {
    if (!fieldDictionary || !Array.isArray(fields)) return;

    fields.forEach(field => {
        if (!field?.id) return;
        fieldDictionary[field.id] = field.value ?? "";
    });
};

export const buildFieldWriterPayload = (fieldDictionary) => {
    return Object.entries(fieldDictionary || {}).map(([id, value]) => ({
        id,
        value
    }));
};

export const updateLateFeeFields = async (accessToken, loanId, fieldDictionary) => {
    if (!loanId) return [];

    try {
        const lateFeeResponse = await getLateFee(accessToken, loanId);

        const regZ = lateFeeResponse?.loan?.regulationZ;

        if (!regZ) {
            console.warn("Late fee regulationZ not found.");
            return [];
        }

        const lateFeeFields = [
            {
                id: "672",
                value: regZ.lateChargeDays != null ? regZ.lateChargeDays.toString() : ""
            },
            {
                id: "674",
                value: regZ.lateChargePercent != null ? regZ.lateChargePercent.toString() : ""
            },
            {
                id: "1719",
                value: regZ.lateChargeType || ""
            },
            {
                id: "2831",
                value: regZ.minimumLateCharge != null ? regZ.minimumLateCharge.toString() : ""
            },
            {
                id: "2832",
                value: regZ.maximumLateCharge != null ? regZ.maximumLateCharge.toString() : ""
            },
            {
                id: "1854",
                value: regZ.lateChargeComments || ""
            }
        ];

        addFieldsToDictionary(fieldDictionary, lateFeeFields);

        info(`Late fee fields collected for LoanID: ${loanId}`, {
            component: "LoanForm",
            function: "updateLateFeeFields",
            loanId
        });

        return lateFeeFields;
    } catch (error) {
        console.error("Late fee field update failed:", error);

        logAppInsightError("Late fee field update failed", {
            problemId: logError("LATE_FEE_UPDATE_FAILED"),
            errorCode: "LATE_FEE_UPDATE_FAILED",
            component: "LoanForm",
            function: "updateLateFeeFields",
            loanId,
            rawError: error?.message || String(error),
            responseData: error?.response?.data || null
        });

        return [];
    }
};

export const updateTPOAEtoLoan = async (salesRepAeList, loanId, fieldDictionary, selectedAeUserId) => {
    if (Array.isArray(salesRepAeList) && salesRepAeList.length === 0) {
        console.warn('No Account Execute is defined in Company.');
        return;
    }

    let selectedAE = selectedAeUserId ? salesRepAeList.find(ae => ae.userId === selectedAeUserId) : salesRepAeList[0];

    if (!selectedAeUserId && selectedAE.isPrimarySalesRep && salesRepAeList.length > 1) {
        selectedAE = salesRepAeList.find(ae => !ae.isPrimarySalesRep);
    }

    const companyAE = [
        {
            id: "TPO.X30",
            value: selectedAE.name
        },
        {
            id: "TPO.X31",
            value: selectedAE.userId
        }
    ];

    info(`Collecting AE fields for LoanID: ${loanId}, Values: ${JSON.stringify(companyAE)}`, {
        component: "LoanForm"
    });

    addFieldsToDictionary(fieldDictionary, companyAE);

    return selectedAE;
};

export const applyAEtoLoan = async (
    accessToken,
    loanId,
    selectedAE,
    aeBdrContact = null,
    shouldUpdateAeBdr = false
) => {
    if (!selectedAE && !(shouldUpdateAeBdr && aeBdrContact)) return;

    const roles = await getMilestoneFreeRoles(accessToken, loanId);
    const payload = [];

    const buildUserPayload = (roleId, user) => {
        const entityId =
            user?.userId ||
            user?.id ||
            user?.entityId ||
            user?.entityID ||
            "";

        if (!entityId) return null;

        return {
            id: roleId,
            loanAssociate: {
                loanAssociateType: "User",
                user: {
                    entityId,
                    entityType: "User"
                },
                email: user?.email || null,
                phone: user?.phone || null,
                cellPhone: user?.cellPhone || null,
                fax: user?.fax || null
            }
        };
    };

    // Existing AE role update
    if (selectedAE) {
        const aeRole = roles.find(r =>
            r?.loanAssociate?.role?.entityName === "Account Executive"
        );

        if (aeRole) {
            const aePayload = buildUserPayload(aeRole.id, selectedAE);
            if (aePayload) payload.push(aePayload);
        } else {
            console.warn("No Account Executive role found for this loan.");
        }
    }

    // New AE-BDR Support role update
    if (shouldUpdateAeBdr && aeBdrContact) {
        const aebdrRole = roles.find(r =>
            r?.loanAssociate?.role?.entityName === "AE-BDR Support"
        );

        if (aebdrRole) {
            const aebdrPayload = buildUserPayload(aebdrRole.id, aeBdrContact);
            if (aebdrPayload) payload.push(aebdrPayload);
        } else {
            console.warn("No AE-BDR Support role found for this loan.");
        }
    }

    if (payload.length === 0) return;

    info(`Updating AE/AE-BDR File Contacts for LoanID: ${loanId}, Values: ${JSON.stringify(payload)}`, {
        component: "LoanForm"
    });

    await patchMilestoneFreeRoles(accessToken, loanId, payload);
};

export const addLoCompensationIfAvailable = (loanObject, selectedCompanyData, selectedLoanOfficer) => {

    if (selectedCompanyData?.loComp?.loCompHistory?.length > 0) {
        const paidCompensation = process.env.REACT_APP_COMPENSATION_PAID;

        const loComp = selectedCompanyData.loComp.loCompHistory[0];

        const { lastAppliedDate, lastAppliedDateTime } = getCurrentPSTDateAndTimeISO();

        loanObject.loCompensation = {
            basePlanAdditonalAmountForBroker: loComp.amount || 0,
            basePlanAmountForBroker: loComp.amount || 0,
            basePlanMaximumAmountForBroker: loComp.maxAmount || 0,
            basePlanMinimumAmountForBroker: loComp.minAmount || 0,
            basePlanRateForBroker: loComp.percentAmt || 0,
            lastAppliedDate,
            lastAppliedDateTime,
            loanAmountType: loComp.percentAmtIsOf || "Base Loan",
            loanOriginatorId: selectedCompanyData.id || null,
            netAdjustedAmountForBroker: loComp.amount || 0,
            planDate: loComp.startDate ? loComp.startDate.split('T')[0] : null,
            planName: loComp.name || null,
            roundingMethod: loComp.rounding || null,
            triggerField: loComp.triggerBasis || null,
            whoPaidCompensation: paidCompensation,
            loanOriginatorName: selectedCompanyData.companyLegalName,
            whoPaidCompensation: "Borrower Paid"
        };
    }
    else {
        loanObject.loCompensation = {
            whoPaidCompensation: "Borrower Paid"
        };
    }

};

export const correspondentLockedImportFields = [
    {
        id: "138",
        name: "Est. prepaid items",
        contractPath: "loan.estimatedPrepaidItemsAmount"
    },
    {
        id: "137",
        name: "Est. closing costs",
        contractPath: "loan.estimatedClosingCostsAmount"
    },
    {
        id: "969",
        name: "PMI, MIP, Funding Fee",
        contractPath: "loan.miAndFundingFeeTotalAmount"
    },
    {
        id: "1093",
        name: "Discount (if Borrower will pay)",
        contractPath: "loan.borrowerPaidDiscountPointsAmount"
    }
];

export const getValueByContractPath = (loanObject, contractPath) => {
    if (!loanObject || !contractPath) return null;

    const path = contractPath.replace(/^loan\./, "");
    return path.split(".").reduce((obj, key) => obj?.[key], loanObject);
};

/*
 * Customer Division (CX.CMS.CUSTOMERPK) - CMS customer PKs differ per company
 * and per environment. The environment comes from REACT_APP_CONFIG_PATH (the
 * same key config_variables/config_resolver.js uses): "*-prod" (champs-prod,
 * sky-prod) is PROD; anything else, including unset, is treated as UAT.
 *
 * assignByAeTeam: true  -> the division is picked from the broker LO's AE team
 *                          (getTeamCustomerPk), as ChampsTPO does.
 *                 false -> the first option is the default (SkyTPO has one).
 */
const CUSTOMER_DIVISIONS = {
    ChampsTPO: {
        assignByAeTeam: true,
        PROD: [
            { value: "381052", text: "BDR Sales" },
            { value: "381053", text: "Champs West" },
            { value: "381054", text: "Outside Sales" },
            { value: "381055", text: "Team Chi" },
            { value: "381056", text: "Team Chris W" },
            { value: "381057", text: "Division 6" }
        ],
        UAT: [
            { value: "126029", text: "BDR Sales" },
            { value: "126030", text: "Champs West" },
            { value: "126031", text: "Outside Sales" },
            { value: "126032", text: "Team Chi" },
            { value: "126033", text: "Team Chris W" },
            { value: "126034", text: "Division 6" }
        ]
    },
    SkyTPO: {
        assignByAeTeam: false,
        PROD: [{ value: "381058", text: "Sky TPO" }],
        UAT: [{ value: "126216", text: "Sky TPO" }]
    }
};

const CUSTOMER_PK_FIELD_ID = "CX.CMS.CUSTOMERPK";

export const getAppEnvironment = () =>
    String(process.env.REACT_APP_CONFIG_PATH || "").trim().toLowerCase().endsWith("-prod") ? "PROD" : "UAT";

const getCustomerDivisionConfig = () => CUSTOMER_DIVISIONS[getOrgConfig().id] || {};

// Dropdown options for this org + environment.
export const getCustomerDivisionOptions = () => getCustomerDivisionConfig()[getAppEnvironment()] || [];

// True when the division comes from the AE team (ChampsTPO); false means use getDefaultCustomerPk().
export const isCustomerDivisionByAeTeam = () => getCustomerDivisionConfig().assignByAeTeam === true;

// First option - the default when the division is not picked by AE team.
export const getDefaultCustomerPk = () => getCustomerDivisionOptions()[0]?.value || "";

// AE team name -> customer PK for this org + environment ("" when no match).
export const getTeamCustomerPk = (team) => {
    const teamName = String(team || "").trim().toLowerCase();
    if (!teamName) return "";

    return getCustomerDivisionOptions().find(o => o.text.toLowerCase() === teamName)?.value || "";
};

// Copy of a form definition with every field matching encompassId given these
// options, wherever the field sits (sections/rows/extraFields).
const applyFieldOptions = (form, encompassId, options) => {
    const withOptions = (node) => {
        if (Array.isArray(node)) return node.map(withOptions);
        if (!node || typeof node !== "object") return node;
        if (node.encompassId === encompassId) return { ...node, options };

        return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, withOptions(value)]));
    };

    return withOptions(form);
};

export const applyCustomerDivisionOptions = (form) =>
    applyFieldOptions(form, CUSTOMER_PK_FIELD_ID, getCustomerDivisionOptions());

/*
 * Service Provider (CX.CMS.SERVICEPROVIDERID) - CMS service provider PKs differ
 * per environment, and the provider names themselves differ slightly between
 * them (FastApp/FirstApp, "&"/"and"), so each list is kept as CMS has it.
 *
 * active: false -> the provider is no longer orderable; the option is kept so
 * existing orders still resolve a name, but it is hidden from the dropdown
 * (Control() in appraisalOrderFormHelpers.js filters on active !== false).
 */
const SERVICE_PROVIDERS = {
    PROD: [
        { value: "507281", text: "All Pro", active: false },
        { value: "507285", text: "Class Valuation" },
        { value: "507287", text: "Core Valuation", active: false },
        { value: "507289", text: "Encore" },
        { value: "507290", text: "Equity Solutions", active: false },
        { value: "507291", text: "FastApp" },
        { value: "507293", text: "Nations Valuation" },
        { value: "507294", text: "NAN" },
        { value: "507296", text: "Nationwide Property & Appraisal" },
        { value: "507298", text: "Property Rate" },
        { value: "507300", text: "Zap Appraisal" },
        { value: "507301", text: "Accurate Group" },
        { value: "507302", text: "ServiceLink", active: false }
    ],
    UAT: [
        { value: "80083", text: "All Pro", active: false },
        { value: "80084", text: "Class Valuation" },
        { value: "80085", text: "Core Valuation", active: false },
        { value: "80086", text: "Encore" },
        { value: "80087", text: "Equity Solutions", active: false },
        { value: "80088", text: "FirstApp" },
        { value: "80089", text: "Nations Valuation" },
        { value: "80090", text: "NAN" },
        { value: "80091", text: "Nationwide Property and Appraisal" },
        { value: "80092", text: "Property Rate" },
        { value: "80093", text: "Zap Appraisal" },
        { value: "80094", text: "Accurate Group" },
        { value: "80095", text: "ServiceLink", active: false }
    ]
};

const SERVICE_PROVIDER_FIELD_ID = "CX.CMS.SERVICEPROVIDERID";

/*
 * SkyTPO is only ever pointed at the PROD CMS, so it uses the PROD providers
 * whatever REACT_APP_CONFIG_PATH says; every other org follows the environment.
 */
const SERVICE_PROVIDER_ENVIRONMENT_BY_ORG = { SkyTPO: "PROD" };

// Dropdown options for this org + environment.
export const getServiceProviderOptions = () =>
    SERVICE_PROVIDERS[SERVICE_PROVIDER_ENVIRONMENT_BY_ORG[getOrgConfig().id] || getAppEnvironment()] || [];

export const applyServiceProviderOptions = (form) =>
    applyFieldOptions(form, SERVICE_PROVIDER_FIELD_ID, getServiceProviderOptions());
